import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth APIs
export const authAPI = {
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  register: (data) => axios.post(`${API}/auth/register`, data),
  me: () => axios.get(`${API}/auth/me`),
  verifyEmail: (token) => axios.post(`${API}/auth/verify-email`, { token }),
  resendVerification: (email) => axios.post(`${API}/auth/resend-verification`, { email }),
  getVerificationStatus: () => axios.get(`${API}/auth/verification-status`),
};

// Promotions APIs
export const promotionsAPI = {
  getAll: () => axios.get(`${API}/promotions`),
  getOne: (id) => axios.get(`${API}/promotions/${id}`),
  create: (data) => axios.post(`${API}/promotions`, data),
  update: (id, data) => axios.put(`${API}/promotions/${id}`, data),
  delete: (id) => axios.delete(`${API}/promotions/${id}`),
};

// Credits APIs
export const creditsAPI = {
  getBalance: () => axios.get(`${API}/credits/balance`),
  getTransactions: (limit = 20) => axios.get(`${API}/credits/transactions?limit=${limit}`),
  claimDailyBonus: () => axios.post(`${API}/credits/daily-bonus`),
  getLeaderboard: (limit = 10) => axios.get(`${API}/credits/leaderboard?limit=${limit}`),
  getReferralStats: () => axios.get(`${API}/credits/referral-stats`),
};

// Feed APIs
export const feedAPI = {
  getFeed: (limit = 10) => axios.get(`${API}/feed?limit=${limit}`),
  recordView: (promotionId) => axios.post(`${API}/feed/view`, { promotion_id: promotionId }),
  recordClick: (promotionId) => axios.post(`${API}/feed/click`, { promotion_id: promotionId }),
  likePromotion: (promotionId) => axios.post(`${API}/feed/like`, { promotion_id: promotionId }),
  contactOwner: (promotionId, subject, message) => axios.post(`${API}/feed/contact-owner`, { promotion_id: promotionId, subject, message }),
};

// AI APIs
export const aiAPI = {
  generateCopy: (topic, tone, category) => axios.post(`${API}/ai/generate-copy`, { topic, tone, category }),
  optimize: (promotionId) => axios.post(`${API}/ai/optimize`, { promotion_id: promotionId }),
  generateImage: (prompt, style) => axios.post(`${API}/ai/generate-image`, { prompt, style }),
  getScore: (promotionId) => axios.get(`${API}/ai/score/${promotionId}`),
  // Landing page AI features
  generateLandingContent: (topic, targetAudience) => axios.post(`${API}/ai/generate-landing-content`, { business_name: topic, business_type: topic, target_audience: targetAudience }),
  generateLandingImage: (businessName, businessType, style = 'professional') => axios.post(`${API}/ai/generate-landing-image`, { business_name: businessName, business_type: businessType || 'business', style }),
};

// Analytics APIs
export const analyticsAPI = {
  getOverview: () => axios.get(`${API}/analytics/overview`),
  getPromotion: (id) => axios.get(`${API}/analytics/promotion/${id}`),
};

// Admin APIs
export const adminAPI = {
  getUsers: (skip = 0, limit = 50) => axios.get(`${API}/admin/users?skip=${skip}&limit=${limit}`),
  getPromotions: (skip = 0, limit = 50) => axios.get(`${API}/admin/promotions?skip=${skip}&limit=${limit}`),
  moderatePromotion: (id, isActive) => axios.put(`${API}/admin/promotions/${id}/moderate`, null, { params: { is_active: isActive } }),
  getSettings: () => axios.get(`${API}/admin/settings`),
  updateSettings: (data) => axios.put(`${API}/admin/settings`, data),
  updateUserPlan: (userId, plan) => axios.put(`${API}/admin/users/${userId}/plan`, null, { params: { plan } }),
  toggleAdmin: (userId, isAdmin) => axios.put(`${API}/admin/users/${userId}/admin`, null, { params: { is_admin: isAdmin } }),
  deleteUser: (userId) => axios.delete(`${API}/admin/users/${userId}`),
  getStats: () => axios.get(`${API}/admin/stats`),
  // Newsletter APIs
  getNewsletters: () => axios.get(`${API}/admin/newsletters`),
  createNewsletter: (data) => axios.post(`${API}/admin/newsletters`, data),
  updateNewsletter: (id, data) => axios.put(`${API}/admin/newsletters/${id}`, data),
  deleteNewsletter: (id) => axios.delete(`${API}/admin/newsletters/${id}`),
  sendNewsletter: (id) => axios.post(`${API}/admin/newsletters/${id}/send`),
  resendNewsletter: (id) => axios.post(`${API}/admin/newsletters/${id}/resend`),
  getNewsletterSends: (id) => axios.get(`${API}/admin/newsletters/${id}/sends`),
  getNewsletterStats: (id) => axios.get(`${API}/admin/newsletters/${id}/stats`),
  generateNewsletterContent: (topic, tone) => axios.post(`${API}/admin/newsletters/generate-content`, { topic, tone }),
  // Video Ads Admin
  getVideoAds: (status) => axios.get(`${API}/admin/video-ads`, { params: status ? { status } : {} }),
  approveVideoAd: (id) => axios.put(`${API}/admin/video-ads/${id}/approve`),
  rejectVideoAd: (id, reason) => axios.put(`${API}/admin/video-ads/${id}/reject`, null, { params: { reason } }),
  getApprovedVideoAds: () => axios.get(`${API}/admin/video-ads/approved`),
  // Site Settings APIs
  getSiteSettings: () => axios.get(`${API}/admin/site-settings`),
  updateSiteSettings: (data) => axios.put(`${API}/admin/site-settings`, data),
  generatePromoVideo: (prompt) => axios.post(`${API}/admin/generate-promo-video`, null, { params: { prompt } }),
};

// Landing Pages APIs
export const landingPagesAPI = {
  getAll: () => axios.get(`${API}/landing-pages`),
  getOne: (id) => axios.get(`${API}/landing-pages/${id}`),
  create: (data) => axios.post(`${API}/landing-pages`, data),
  update: (id, data) => axios.put(`${API}/landing-pages/${id}`, data),
  delete: (id) => axios.delete(`${API}/landing-pages/${id}`),
  getPublic: (slug) => axios.get(`${API}/p/${slug}`),
  submitLead: (slug, data) => axios.post(`${API}/p/${slug}/lead`, data),
};

// Leads APIs
export const leadsAPI = {
  getAll: (landingPageId = null) => axios.get(`${API}/leads${landingPageId ? `?landing_page_id=${landingPageId}` : ''}`),
  getStats: () => axios.get(`${API}/leads/stats`),
  getOne: (id) => axios.get(`${API}/leads/${id}`),
  updateNotes: (id, notes) => axios.put(`${API}/leads/${id}/notes`, null, { params: { notes } }),
  delete: (id) => axios.delete(`${API}/leads/${id}`),
  addManual: (data) => {
    const fd = new FormData();
    fd.append('email', data.email);
    fd.append('landing_page_id', data.landing_page_id);
    if (data.name) fd.append('name', data.name);
    if (data.phone) fd.append('phone', data.phone);
    return axios.post(`${API}/leads/manual`, fd);
  },
  importCsv: (file, landingPageId) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('landing_page_id', landingPageId);
    return axios.post(`${API}/leads/import-csv`, fd);
  },
  bulkDelete: (leadIds) => axios.post(`${API}/leads/bulk-delete`, { lead_ids: leadIds }),
};

// Messages APIs
export const messagesAPI = {
  send: (data) => axios.post(`${API}/messages`, data),
  getAll: (leadId = null) => axios.get(`${API}/messages${leadId ? `?lead_id=${leadId}` : ''}`),
  // Scheduled messages
  getTimezones: () => axios.get(`${API}/messages/timezones`),
  createScheduled: (data) => axios.post(`${API}/messages/scheduled`, data),
  getScheduled: () => axios.get(`${API}/messages/scheduled`),
  getScheduledOne: (id) => axios.get(`${API}/messages/scheduled/${id}`),
  previewScheduled: (id) => axios.get(`${API}/messages/scheduled/${id}/preview`),
  updateScheduled: (id, data) => axios.put(`${API}/messages/scheduled/${id}`, data),
  deleteScheduled: (id) => axios.delete(`${API}/messages/scheduled/${id}`),
};

// Payments APIs
export const paymentsAPI = {
  createCheckout: (packageId) => axios.post(`${API}/payments/create-checkout`, { 
    package_id: packageId, 
    origin_url: window.location.origin 
  }),
  getStatus: (sessionId) => axios.get(`${API}/payments/status/${sessionId}`),
  getHistory: () => axios.get(`${API}/payments/history`),
  getPackages: () => axios.get(`${API}/payments/packages`),
};

// Video Ads APIs
export const videoAdsAPI = {
  getMyAds: () => axios.get(`${API}/video-ads`),
  submitAd: (data) => axios.post(`${API}/video-ads`, data),
  getPricing: () => axios.get(`${API}/video-ads/pricing`),
  createCheckout: (data) => axios.post(`${API}/video-ads/create-checkout`, data),
};

// Announcements APIs
export const announcementsAPI = {
  // Admin
  create: (data) => axios.post(`${API}/announcements`, data),
  getAll: () => axios.get(`${API}/announcements/admin`),
  update: (id, data) => axios.put(`${API}/announcements/${id}`, data),
  delete: (id) => axios.delete(`${API}/announcements/${id}`),
  // User
  getFeed: () => axios.get(`${API}/announcements/feed`),
  markRead: (id) => axios.post(`${API}/announcements/${id}/read`),
  markAllRead: () => axios.post(`${API}/announcements/read-all`),
};

export default {
  auth: authAPI,
  promotions: promotionsAPI,
  credits: creditsAPI,
  feed: feedAPI,
  ai: aiAPI,
  analytics: analyticsAPI,
  admin: adminAPI,
  landingPages: landingPagesAPI,
  leads: leadsAPI,
  messages: messagesAPI,
  payments: paymentsAPI,
  videoAds: videoAdsAPI,
  announcements: announcementsAPI,
};
